import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import { ArrowRight, Megaphone, Hammer, Buildings, Storefront, ChartLineUp, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const PACKAGES = [
    {
        id: "spotlight",
        name: "Spotlight",
        price: "£99",
        unit: "/ month",
        slot: "discover_top",
        description: "Top-of-page banner across the Discover feed. High visibility for merchants targeting active builders.",
        features: ["Hero slot on /discover", "~10–15k monthly impressions", "Click tracking", "1 ad creative"],
        highlight: false,
    },
    {
        id: "trade_partner",
        name: "Trade Partner",
        price: "£249",
        unit: "/ month",
        slot: "feed",
        description: "In-feed sponsored card displayed alongside builder profiles. Native, non-intrusive placement.",
        features: ["In-feed card (Discover)", "Featured on landing page", "Unlimited creative swaps", "Monthly performance report"],
        highlight: true,
    },
    {
        id: "anchor",
        name: "Anchor",
        price: "£499",
        unit: "/ month",
        slot: "hero",
        description: "Hero billboard on the homepage. Reach every visitor with full-bleed creative.",
        features: ["Homepage hero billboard", "Featured on builder profiles", "Co-marketing post (1/mo)", "Priority support"],
        highlight: false,
    },
];

export default function Advertise() {
    const [pkg, setPkg] = useState("trade_partner");
    const [form, setForm] = useState({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        budget: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/inquiries", { ...form, package: pkg });
            setDone(true);
            toast.success("Inquiry sent — we'll be in touch within 1 business day");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send inquiry");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="hero-bg">
            {/* HERO */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-16">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8 fade-in-up">
                        <div className="inline-flex items-center gap-2 mb-6 border border-black px-3 py-1.5">
                            <Megaphone size={14} weight="fill" />
                            <span className="text-overline">Advertise on BuildersGallery</span>
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] uppercase">
                            Reach UK<br />
                            <span className="text-[#E63946]">builders & trades</span><br />
                            where they work.
                        </h1>
                        <p className="mt-8 max-w-2xl text-base sm:text-lg text-neutral-600 leading-relaxed">
                            Merchants, building suppliers, tool brands and trade insurers — put your products in front
                            of an audience that buys daily. Native, sharp-edged ad slots. No popups. No retargeting tricks.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#packages" className="btn-accent">View packages <ArrowRight size={16} weight="bold" /></a>
                            <a href="#contact" className="btn-ghost">Talk to sales</a>
                        </div>
                    </div>
                    <div className="md:col-span-4 grid grid-cols-3 gap-4">
                        {[
                            { v: "100%", l: "Trade audience" },
                            { v: "0", l: "Popups / spam" },
                            { v: "1d", l: "Setup time" },
                        ].map((s) => (
                            <div key={s.l} className="border border-black p-4 bg-white">
                                <div className="font-display text-3xl font-black leading-none">{s.v}</div>
                                <div className="text-overline text-neutral-600 mt-2">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="text-overline text-[#E63946] mb-2">Who advertises with us</div>
                <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter uppercase mb-10">
                    Built for the trade ecosystem.
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-black">
                    {[
                        { icon: Storefront, title: "Builders' merchants", text: "Bricks, timber, plasterboard, fixings." },
                        { icon: Hammer, title: "Tool & PPE brands", text: "Power tools, workwear, safety gear." },
                        { icon: Buildings, title: "Suppliers & manufacturers", text: "Kitchens, windows, doors, roofing." },
                        { icon: ChartLineUp, title: "Insurance & finance", text: "Trade insurance, business banking, vans." },
                    ].map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.title} className={`p-6 bg-white ${i < 3 ? "border-b lg:border-b-0 lg:border-r" : ""} ${i === 3 ? "" : "sm:[&:nth-child(2)]:border-r-0 sm:border-r"} border-black`}>
                                <Icon size={28} weight="duotone" className="mb-4" />
                                <h3 className="font-display text-lg font-bold mb-1">{c.title}</h3>
                                <p className="text-sm text-neutral-600">{c.text}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* AD PREVIEW (live) */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="text-overline text-[#E63946] mb-2">Live preview</div>
                <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter uppercase mb-8">
                    Where your ad shows.
                </h2>
                <AdSlot slot="hero" variant="banner" />
                <div className="mt-8">
                    <AdSlot slot="feed" variant="card" limit={2} />
                </div>
                <p className="text-xs text-neutral-500 mt-4">If you see no preview, no live campaigns are running yet — you can be the first.</p>
            </section>

            {/* PACKAGES */}
            <section id="packages" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="text-overline text-[#E63946] mb-2">Packages</div>
                <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase mb-12">
                    Simple, flat pricing.
                </h2>
                <div className="grid md:grid-cols-3 gap-0 border border-black">
                    {PACKAGES.map((p, i) => (
                        <div
                            key={p.id}
                            className={`p-8 bg-white relative ${i < 2 ? "md:border-r border-black" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} ${p.highlight ? "bg-black text-white" : ""}`}
                        >
                            {p.highlight && (
                                <div className="absolute top-0 right-0 bg-[#E63946] text-white text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1">
                                    Most popular
                                </div>
                            )}
                            <div className={`text-overline mb-3 ${p.highlight ? "text-[#E63946]" : "text-[#E63946]"}`}>{p.name}</div>
                            <div className="font-display text-5xl font-black tracking-tighter leading-none">
                                {p.price}
                                <span className={`text-base font-medium ml-2 ${p.highlight ? "text-neutral-400" : "text-neutral-500"}`}>{p.unit}</span>
                            </div>
                            <p className={`mt-4 text-sm leading-relaxed ${p.highlight ? "text-neutral-300" : "text-neutral-600"}`}>{p.description}</p>
                            <ul className="mt-6 space-y-2">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm">
                                        <Check size={16} weight="bold" className="text-[#E63946] mt-0.5 flex-shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => {
                                    setPkg(p.id);
                                    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                data-testid={`select-package-${p.id}`}
                                className={`mt-6 w-full justify-center ${p.highlight ? "btn-accent" : "btn-primary"}`}
                            >
                                Choose {p.name}
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="text-overline text-[#E63946] mb-2">Talk to us</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none mb-6">
                            Let's get<br />you on site.
                        </h2>
                        <p className="text-neutral-600 leading-relaxed mb-8">
                            Tell us a bit about your business and the package you're interested in. Our team replies
                            within one business day with a media kit and next steps.
                        </p>
                        <div className="border-l-2 border-[#E63946] pl-4 text-sm text-neutral-700">
                            Already a builder?{" "}
                            <Link to="/register" className="underline font-semibold">Create your free gallery →</Link>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        {done ? (
                            <div className="border border-black p-10 bg-white text-center fade-in-up" data-testid="inquiry-success">
                                <Check size={36} weight="bold" className="mx-auto mb-3 text-[#E63946]" />
                                <h3 className="font-display text-2xl font-black uppercase mb-2">Inquiry received</h3>
                                <p className="text-neutral-600">Thanks — we'll be in touch within 1 business day.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="advertise-inquiry-form" className="border border-black p-6 sm:p-8 bg-white space-y-5">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Company *</label>
                                        <input
                                            required
                                            value={form.company_name}
                                            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                            className="input-field"
                                            data-testid="inquiry-company-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Your name *</label>
                                        <input
                                            required
                                            value={form.contact_name}
                                            onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                            className="input-field"
                                            data-testid="inquiry-name-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Email *</label>
                                        <input
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className="input-field"
                                            data-testid="inquiry-email-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Phone</label>
                                        <input
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            className="input-field"
                                            data-testid="inquiry-phone-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Package</label>
                                        <select
                                            value={pkg}
                                            onChange={(e) => setPkg(e.target.value)}
                                            className="input-field"
                                            data-testid="inquiry-package-input"
                                        >
                                            {PACKAGES.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name} — {p.price}{p.unit}
                                                </option>
                                            ))}
                                            <option value="custom">Custom / not sure</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-overline text-neutral-700 block mb-2">Monthly budget</label>
                                        <input
                                            value={form.budget}
                                            onChange={(e) => setForm({ ...form, budget: e.target.value })}
                                            placeholder="£500"
                                            className="input-field"
                                            data-testid="inquiry-budget-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">Message</label>
                                    <textarea
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        className="input-field resize-none"
                                        placeholder="Tell us about your business and goals…"
                                        data-testid="inquiry-message-input"
                                    />
                                </div>
                                <button type="submit" disabled={submitting} className="btn-accent" data-testid="inquiry-submit-btn">
                                    {submitting ? "Sending…" : "Send inquiry"} <ArrowRight size={16} weight="bold" />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
